/**
 * Settings → Fuel → RC Lookup
 *
 * Admin control panel for the Invincible Ocean RTO API integration.
 * When configured, the "Fetch RC" button on vehicle forms auto-fills
 * make/model/fuel type/insurance/PUC. Without a key it falls back to
 * manual entry (fully functional — just no auto-fill).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Car, Eye, EyeOff, Save, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { apiGet, apiPut, apiPost } from '../../lib/api';

export default function FuelRCLookup() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ enabled: false, api_key_configured: false, provider: 'invincibleocean' });
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testPlate, setTestPlate] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // need a vehicle id for test — find first vehicle
  const [firstVehicleId, setFirstVehicleId] = useState(null);

  useEffect(() => {
    apiGet('/fuel/rc-lookup-config').then((d) => {
      setCfg(d || {});
      setEnabled(d?.enabled || false);
    }).catch(() => {});
    apiGet('/fuel/vehicles?limit=1').then((d) => {
      const items = d?.items || [];
      if (items[0]) setFirstVehicleId(items[0].id);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/fuel/rc-lookup-config', { enabled, api_key: apiKey || undefined });
      toast.success('RC Lookup settings saved');
      const fresh = await apiGet('/fuel/rc-lookup-config');
      setCfg(fresh || {});
      setApiKey('');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false); }
  };

  const test = async () => {
    if (!testPlate.trim()) { toast.error('Enter a registration number to test'); return; }
    if (!firstVehicleId) { toast.error('No vehicles found — add a vehicle first'); return; }
    setTesting(true); setTestResult(null);
    try {
      const r = await apiPost(`/fuel/vehicles/${firstVehicleId}/rc-fetch`,
        { registration_number: testPlate.trim() });
      setTestResult(r);
    } catch (e) {
      setTestResult({ source: 'error', message: e?.response?.data?.detail || 'Request failed' });
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/app/settings')}
          className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 mb-4">
          <ArrowLeft size={14} /> Settings
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#3b4872]/10 flex items-center justify-center">
            <Car size={20} className="text-[#3b4872]" />
          </div>
          <div>
            <h1 className="font-display font-bold text-xl text-slate-900">Fuel · RC Lookup</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Auto-fill vehicle details (make, model, fuel type, insurance, PUC) from the RTO database.
            </p>
          </div>
        </div>
      </div>

      {/* Status banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${
        cfg.api_key_configured && cfg.enabled
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}>
        {cfg.api_key_configured && cfg.enabled
          ? <><CheckCircle size={16} className="text-emerald-600 shrink-0" />
              RC auto-fetch is <strong>active</strong>. The "Fetch RC" button on vehicle forms will call the Invincible Ocean API.</>
          : <><XCircle size={16} className="text-amber-600 shrink-0" />
              RC auto-fetch is <strong>not active</strong>. Vehicle forms fall back to manual entry.</>
        }
      </div>

      {/* Config card */}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {/* Enabled toggle */}
        <div className="flex items-center justify-between px-5 py-4">
          <div>
            <div className="font-semibold text-sm text-slate-800">Enable RC auto-fetch</div>
            <div className="text-xs text-slate-500 mt-0.5">When on, the "Fetch RC" button calls the RTO API using the key below.</div>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? 'bg-[#3b4872]' : 'bg-slate-200'
            }`}
            data-testid="rc-enabled-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* API key */}
        <div className="px-5 py-4 space-y-2">
          <label className="block text-sm font-semibold text-slate-700">
            Invincible Ocean API Key
            {cfg.api_key_configured && (
              <span className="ml-2 text-xs font-normal text-emerald-600">(currently set — paste new key to update)</span>
            )}
          </label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.api_key_configured ? '•••••••••••••••• (leave blank to keep existing)' : 'Paste your Invincible Ocean Client ID'}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-[#3b4872]/30 focus:border-[#3b4872]"
              data-testid="rc-api-key-input"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
            >
              {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-xs text-slate-400">
            Get a key at{' '}
            <a href="https://www.invincibleocean.com" target="_blank" rel="noopener noreferrer"
              className="underline text-[#3b4872]">invincibleocean.com</a>.
            Verify the <code className="bg-slate-100 px-1 rounded">vehicleRC</code> endpoint field names
            before activating (<code>maker_model</code>, <code>fuel_type</code>, <code>insurance_upto</code>,
            <code>pucc_upto</code>, <code>fit_upto</code>, <code>reg_authority</code>, <code>vehicle_class</code>).
          </p>
        </div>

        {/* Provider */}
        <div className="px-5 py-4">
          <div className="text-xs text-slate-500">
            Provider: <strong>Invincible Ocean</strong> · Endpoint: <code className="bg-slate-100 px-1 rounded text-xs">POST /invincible/vehicleRC</code>
          </div>
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-2.5 rounded-lg text-sm font-display font-bold transition-colors disabled:opacity-50"
        data-testid="rc-save-btn"
      >
        {saving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
        Save Settings
      </button>

      {/* Test section */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <div className="font-semibold text-sm text-slate-800">Test RC Fetch</div>
        <p className="text-xs text-slate-500">
          Send a one-off test call and see what the API returns. This writes the registration number
          to the first vehicle in your fleet.
        </p>
        <div className="flex gap-2">
          <input
            value={testPlate}
            onChange={(e) => setTestPlate(e.target.value.toUpperCase())}
            placeholder="MH12AB1234"
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
            data-testid="rc-test-plate-input"
          />
          <button
            onClick={test}
            disabled={testing || !cfg.api_key_configured}
            className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-display font-bold disabled:opacity-40"
            data-testid="rc-test-btn"
            title={!cfg.api_key_configured ? 'Save an API key first' : ''}
          >
            {testing ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Test
          </button>
        </div>

        {testResult && (
          <div className={`rounded-md p-3 text-xs font-mono whitespace-pre-wrap ${
            testResult.source === 'rto_api' ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'
          }`} data-testid="rc-test-result">
            <div className={`font-sans font-bold text-xs mb-2 ${
              testResult.source === 'rto_api' ? 'text-emerald-700' : 'text-slate-600'
            }`}>
              {testResult.source === 'rto_api' ? '✓ API response' : 'ℹ Manual entry fallback'}
            </div>
            {testResult.message}
            {testResult.fetched && (
              <div className="mt-2 space-y-0.5">
                {Object.entries(testResult.fetched).map(([k, v]) => (
                  <div key={k}><span className="text-slate-400">{k}:</span> {v}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
